import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { FieldRow, TextInput, TextareaInput } from "@/components/ui/Field";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { searchOrders } from "@/lib/projectOverview";
import type { AuftragLookup } from "@/lib/material";
import {
  createCompanyEvent,
  updateCompanyEvent,
  deleteCompanyEvent,
  type PlanungAssignment,
  type PlanungOrderLink,
  type PlanungRow,
} from "@/lib/montageplanung";
import { rowLabelFull } from "./displayLabels";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";
import type { ConfirmDialogOptions } from "./useConfirmDialog";

type ParticipantMode = "all" | "selected";

function toAuftragLookup(o: {
  id: number;
  auftragsnummer?: string;
  kunde?: string;
  beschreibung?: string;
  jahr?: number;
  ort?: string;
}): AuftragLookup {
  return {
    id: o.id,
    auftragsnummer: o.auftragsnummer?.trim() || String(o.id),
    jahr: o.jahr ?? null,
    kunde: o.kunde ?? null,
    ort: o.ort ?? null,
    beschreibung: o.beschreibung ?? null,
    ort_zusatz: null,
  };
}

function participantRowIdsFromAssignment(
  assignment: PlanungAssignment,
  allAssignments: PlanungAssignment[],
): number[] {
  const memberIds = new Set<number>([
    assignment.id,
    ...(assignment.linked_assignment_ids ?? []),
  ]);
  return allAssignments
    .filter((a) => memberIds.has(a.id))
    .map((a) => a.row_id)
    .sort((a, b) => a - b);
}

export interface CompanyEventDialogProps {
  open: boolean;
  onClose: () => void;
  employeeRows: PlanungRow[];
  boardAssignments: PlanungAssignment[];
  defaultDate: string;
  preselectedRowId?: number | null;
  assignment?: PlanungAssignment | null;
  onSaved: () => void;
  onHistory?: () => void;
  onConfirm?: (options: ConfirmDialogOptions) => Promise<boolean>;
}

export function CompanyEventDialog({
  open,
  onClose,
  employeeRows,
  boardAssignments,
  defaultDate,
  preselectedRowId,
  assignment = null,
  onSaved,
  onHistory,
  onConfirm,
}: CompanyEventDialogProps) {
  const isEdit = assignment != null;
  const [title, setTitle] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [startTime, setStartTime] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [orders, setOrders] = useState<PlanungOrderLink[]>([]);
  const [orderQuery, setOrderQuery] = useState("");
  const [orderResults, setOrderResults] = useState<AuftragLookup[]>([]);
  const [orderSearching, setOrderSearching] = useState(false);
  const [mode, setMode] = useState<ParticipantMode>("all");
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimer = useRef<number | null>(null);

  const sortedRows = useMemo(
    () =>
      [...employeeRows].sort((a, b) =>
        rowLabelFull(a).localeCompare(rowLabelFull(b), "de"),
      ),
    [employeeRows],
  );

  const selectedOrderIds = useMemo(() => new Set(orders.map((o) => o.order_id)), [orders]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setOrderQuery("");
    setOrderResults([]);

    if (assignment) {
      setTitle(assignment.title);
      setDateFrom(assignment.date_from.slice(0, 10));
      setDateTo(assignment.date_to.slice(0, 10));
      setStartTime(assignment.event_start_time ?? "");
      setAddress(assignment.event_address ?? "");
      setNote(assignment.note ?? "");
      setOrders(assignment.orders ?? []);
      const participantIds = participantRowIdsFromAssignment(assignment, boardAssignments);
      if (participantIds.length >= sortedRows.length) {
        setMode("all");
        setSelectedRowIds(new Set());
      } else {
        setMode("selected");
        setSelectedRowIds(new Set(participantIds));
      }
      return;
    }

    setTitle("");
    setDateFrom(defaultDate);
    setDateTo(defaultDate);
    setStartTime("");
    setAddress("");
    setNote("");
    setOrders([]);
    if (preselectedRowId) {
      setMode("selected");
      setSelectedRowIds(new Set([preselectedRowId]));
    } else {
      setMode("all");
      setSelectedRowIds(new Set());
    }
  }, [open, defaultDate, preselectedRowId, assignment, boardAssignments, sortedRows.length]);

  useEffect(() => {
    if (!open) return;
    const q = orderQuery.trim();
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    if (q.length < 2) {
      setOrderResults([]);
      setOrderSearching(false);
      return;
    }
    setOrderSearching(true);
    searchTimer.current = window.setTimeout(async () => {
      try {
        const results = await searchOrders(q, 8);
        setOrderResults(results.map(toAuftragLookup));
      } catch {
        setOrderResults([]);
      } finally {
        setOrderSearching(false);
      }
    }, 250);
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, [orderQuery, open]);

  function toggleRow(rowId: number) {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }

  function addOrder(o: AuftragLookup) {
    if (selectedOrderIds.has(o.id)) return;
    setOrders((prev) => [...prev, { order_id: o.id, order_label: formatAuftragLookupLabel(o) }]);
    setOrderQuery("");
    setOrderResults([]);
  }

  function removeOrder(orderId: number) {
    setOrders((prev) => prev.filter((o) => o.order_id !== orderId));
  }

  function buildPayload() {
    return {
      title: title.trim(),
      date_from: dateFrom,
      date_to: dateTo,
      event_start_time: startTime.trim() || null,
      event_address: address.trim() || null,
      note: note.trim() || null,
      orders,
      row_ids: mode === "all" ? [] : [...selectedRowIds],
    };
  }

  async function handleSave() {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Bitte einen Titel eingeben.");
      return;
    }
    if (dateTo < dateFrom) {
      setError("Das Enddatum darf nicht vor dem Startdatum liegen.");
      return;
    }
    if (mode === "selected" && selectedRowIds.size === 0) {
      setError("Bitte mindestens einen Mitarbeiter auswählen.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      if (isEdit && assignment) {
        await updateCompanyEvent(assignment.id, payload);
      } else {
        await createCompanyEvent(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  // Spiegelt exakt die Vorbelegung aus dem Reset-Effekt beim Öffnen.
  const dirtyBaseline = useMemo(() => {
    if (assignment) {
      const participantIds = participantRowIdsFromAssignment(assignment, boardAssignments);
      const allSelected = participantIds.length >= sortedRows.length;
      return {
        title: assignment.title,
        dateFrom: assignment.date_from.slice(0, 10),
        dateTo: assignment.date_to.slice(0, 10),
        startTime: assignment.event_start_time ?? "",
        address: assignment.event_address ?? "",
        note: assignment.note ?? "",
        orders: assignment.orders ?? [],
        mode: (allSelected ? "all" : "selected") as ParticipantMode,
        selectedRowIds: allSelected ? [] : [...participantIds].sort((a, b) => a - b),
      };
    }
    return {
      title: "",
      dateFrom: defaultDate,
      dateTo: defaultDate,
      startTime: "",
      address: "",
      note: "",
      orders: [] as PlanungOrderLink[],
      mode: (preselectedRowId ? "selected" : "all") as ParticipantMode,
      selectedRowIds: preselectedRowId ? [preselectedRowId] : [],
    };
  }, [assignment, boardAssignments, sortedRows.length, defaultDate, preselectedRowId]);

  const dirty = hasFormChanges(
    {
      title,
      dateFrom,
      dateTo,
      startTime,
      address,
      note,
      orders,
      mode,
      selectedRowIds: [...selectedRowIds].sort((a, b) => a - b),
    },
    dirtyBaseline,
  );

  async function handleDelete() {
    if (!assignment) return;
    const ok = onConfirm
      ? await onConfirm({
          title: "Betriebsveranstaltung löschen",
          message:
            "Möchten Sie diese Betriebsveranstaltung für alle Teilnehmer wirklich löschen?",
          confirmLabel: "Löschen",
          variant: "danger",
        })
      : window.confirm("Betriebsveranstaltung für alle Teilnehmer wirklich löschen?");
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      await deleteCompanyEvent(assignment.id);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={isEdit ? "Betriebsveranstaltung bearbeiten" : "Betriebsveranstaltung anlegen"}
      description="Fixer Termin für alle oder ausgewählte Mitarbeiter – ohne Feinplanung."
      size="lg"
      fullScreenMobile
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      footer={
        <>
          {isEdit ? (
            <div className="mr-auto flex flex-wrap gap-2">
              <Button variant="danger" onClick={() => void handleDelete()} disabled={saving}>
                Löschen
              </Button>
              {onHistory ? (
                <Button variant="secondary" onClick={onHistory} disabled={saving}>
                  Verlauf
                </Button>
              ) : null}
            </div>
          ) : null}
          <DialogCancelButton onClose={onClose} disabled={saving} />
          <Button variant="primary" onClick={() => void handleSave()} loading={saving}>
            {isEdit ? "Speichern" : "Anlegen"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        {isEdit && (
          <Alert variant="info">
            Betriebsveranstaltungen sind fixe Termine. Änderungen gelten für alle Teilnehmer.
          </Alert>
        )}

        <TextInput
          label="Titel"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="z. B. Betriebsfeier, Schulung, Teambuilding"
          autoFocus
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label="Von"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              if (dateTo < e.target.value) setDateTo(e.target.value);
            }}
          />
          <TextInput
            label="Bis"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label="Startzeit (optional)"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
          <TextInput
            label="Adresse (optional)"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="z. B. Firmengelände, Musterstraße 1"
          />
        </div>

        <TextareaInput
          label="Programm / Beschreibung (optional)"
          hint="Programmpunkte oder kurze Hinweise zur Veranstaltung."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
        />

        <FieldRow
          label="Verknüpfte Projekte (optional)"
          hint="Arbeitszeiten können auf diese Aufträge gebucht werden."
        >
          {orders.length > 0 && (
            <ul className="mb-2 space-y-1">
              {orders.map((o) => (
                <li
                  key={o.order_id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-separator-opaque px-3 py-2 text-sm"
                >
                  <span className="min-w-0 line-clamp-2 break-words">
                    {o.order_label || `Auftrag ${o.order_id}`}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 text-status-red hover:underline"
                    onClick={() => removeOrder(o.order_id)}
                  >
                    Entfernen
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="relative">
            <input
              className="eh-input"
              value={orderQuery}
              onChange={(e) => setOrderQuery(e.target.value)}
              placeholder="Projekt suchen und hinzufügen…"
            />
            {(orderSearching || orderResults.length > 0) && orderQuery.trim().length >= 2 && (
              <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-separator-opaque bg-card shadow-ios-md">
                {orderSearching && (
                  <div className="px-3 py-2 text-sm text-label-secondary">Suche…</div>
                )}
                {!orderSearching &&
                  orderResults
                    .filter((o) => !selectedOrderIds.has(o.id))
                    .map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm line-clamp-2 break-words hover:bg-fill-tertiary"
                        onClick={() => addOrder(o)}
                      >
                        {formatAuftragLookupLabel(o)}
                      </button>
                    ))}
                {!orderSearching &&
                  orderResults.filter((o) => !selectedOrderIds.has(o.id)).length === 0 && (
                    <div className="px-3 py-2 text-sm text-label-secondary">Keine Treffer</div>
                  )}
              </div>
            )}
          </div>
        </FieldRow>

        <FieldRow label="Teilnehmer">
          <div className="space-y-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-label">
              <input
                type="radio"
                name="company-event-participants"
                checked={mode === "all"}
                onChange={() => setMode("all")}
              />
              Alle Mitarbeiter ({sortedRows.length})
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-label">
              <input
                type="radio"
                name="company-event-participants"
                checked={mode === "selected"}
                onChange={() => setMode("selected")}
              />
              Ausgewählte Mitarbeiter
            </label>

            {mode === "selected" && (
              <ul className="max-h-52 space-y-1 overflow-y-auto rounded-lg border border-separator-opaque p-2">
                {sortedRows.map((row) => (
                  <li key={row.id}>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-fill-tertiary">
                      <input
                        type="checkbox"
                        checked={selectedRowIds.has(row.id)}
                        onChange={() => toggleRow(row.id)}
                      />
                      <span>{rowLabelFull(row)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </FieldRow>
      </div>
    </Dialog>
  );
}
